import React, { useCallback, useState } from 'react'
import cn from 'classnames'
import { WbIcon } from '@workbench/react/dist'
import classes from './Vehicle.module.scss'
import { Button, Counter } from 'components'
import { Vehicle as VehicleT } from 'api/types'
import { calculatePrice } from './priceUtils'
import {
  formatPrice,
  formatKilometers,
  formatArea,
  formatVolume,
} from 'utils/format'
import { vehicleImage } from 'utils/vehicleImage'
import { useTranslation } from 'react-i18next'

interface VehicleProps {
  vehicle: VehicleT
  days: number
  onChange: (id: string, count: number) => void
  selectedId?: string
  initialCount?: number
}

export const Vehicle: React.FunctionComponent<VehicleProps> = ({
  vehicle,
  days,
  onChange,
  selectedId,
  initialCount = 1,
}) => {
  const { t } = useTranslation()
  const touched = !!selectedId
  const selected = selectedId === vehicle.id

  const [count, setCount] = useState(initialCount)
  const onClick = useCallback(() => {
    if (vehicle.available && !selected) {
      onChange(vehicle.id, count)
    }
  }, [vehicle.available, onChange, vehicle.id, selected, count])
  const onChangeCount = useCallback(
    (count: number) => {
      if (vehicle.available && (!touched || selected)) {
        setCount(count)
        onChange(vehicle.id, count)
      }
    },
    [vehicle, touched, selected, setCount, onChange]
  )

  const selectText = (
    available: boolean,
    touched: boolean,
    selected: boolean
  ) => {
    if (!available) {
      return t('pages.bookings.bookingForm.vehicle.selectText.notAvailable')
    } else if (touched && selected) {
      return t('pages.bookings.bookingForm.vehicle.selectText.selected')
    } else {
      return t('pages.bookings.bookingForm.vehicle.selectText.select')
    }
  }

  return (
    <div
      className={cn(classes.vehicle, 'wb-card', 'wb-card--smoke', {
        [classes.selected]: selected,
        [classes.disabled]: (touched && !selected) || !vehicle.available,
      })}
      onClick={onClick}
    >
      <div className={classes.thumbnail}>
        <img
          src={vehicleImage(vehicle.group)}
          alt="Thumbnail Sprinter"
          className={classes.thumbnailImage}
        />

        <div className={classes.icon}>
          <WbIcon
            name={vehicle.available && selected ? 'checkmark-circle' : 'close'}
          />
        </div>
      </div>
      <div className={classes.vehicleContent}>
        <div className={classes.vehicleTopContent}>
          <h6>{vehicle.group}</h6>

          {!vehicle.available && (
            <p className={classes.description}>
              {t('pages.bookings.bookingForm.vehicle.notAvailable')}
            </p>
          )}
        </div>

        <div className={classes.vehicleBottomContent}>
          <table className={classes.properties}>
            <tbody>
              <tr>
                <td className={classes.propertyLabel}>
                  {t(
                    'pages.bookings.bookingForm.vehicle.contentTable.loadingArea'
                  )}
                </td>
                <td className={classes.propertyValue}>
                  {formatArea(vehicle.loadingAreaMin)}{' '}
                  {t('pages.bookings.bookingForm.vehicle.contentTable.until')}{' '}
                  {formatArea(vehicle.loadingAreaMax)}
                </td>
              </tr>
              <tr>
                <td className={classes.propertyLabel}>
                  {t('pages.bookings.bookingForm.vehicle.contentTable.volume')}
                </td>
                <td className={classes.propertyValue}>
                  {formatVolume(vehicle.volumeMin)}{' '}
                  {t('pages.bookings.bookingForm.vehicle.contentTable.until')}{' '}
                  {formatVolume(vehicle.volumeMax)}
                </td>
              </tr>
              <tr>
                <td className={classes.propertyLabel}>
                  {t(
                    'pages.bookings.bookingForm.vehicle.contentTable.dailyRent'
                  )}
                </td>
                <td className={classes.propertyValue}>
                  {formatPrice(vehicle.price.dailyRate)} / {t('global.day')}
                </td>
              </tr>
              <tr>
                <td className={classes.propertyLabel}>
                  {t(
                    'pages.bookings.bookingForm.vehicle.contentTable.monthlyRent'
                  )}
                </td>
                <td className={classes.propertyValue}>
                  {formatPrice(vehicle.price.monthlyRate)} / {t('global.day')}
                </td>
              </tr>
              <tr>
                <td className={classes.propertyLabel}>
                  {t(
                    'pages.bookings.bookingForm.vehicle.contentTable.freeKilometer'
                  )}
                </td>
                <td className={classes.propertyValue}>
                  {formatKilometers(vehicle.includedKilometers / days)}
                </td>
              </tr>
            </tbody>
          </table>

          <div className={classes.price}>
            <small>
              {t('pages.bookings.bookingForm.vehicle.contentTable.price', {
                days,
              })}
            </small>
            <div className={classes.priceText}>
              {formatPrice(calculatePrice(vehicle, count))}
            </div>
          </div>

          <div className={classes.footer}>
            <Counter
              id={`counter-${vehicle.group}`}
              label={`${vehicle.group} counter`}
              disabled={!vehicle.available || (touched && !selected)}
              onChange={onChangeCount}
              initialValue={count}
              minCount={1}
            />

            <Button
              id={`select-${vehicle.group}`}
              onClick={onClick}
              disabled={!vehicle.available || (touched && selected)}
              size="small"
              variant="secondary"
            >
              {selectText(vehicle.available, touched, selected)}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
